import { useCallback } from 'react';
import classnames from 'classnames';
import { useAlertSnapshotOverride } from 'src/providers/AlertCenterProvider/useAlertSnapshotOverride.hook';
import { writeAlertSnapshotOverride } from 'src/providers/AlertCenterProvider/AlertSnapshotOverride.store';
import { ALERT_PRESETS, type tAlertPreset } from './AlertSimulator.presets';

const DECLARATION_PRESETS = ALERT_PRESETS.filter((p) => p.kind === 'declaration');
const ADVERSARIAL_PRESETS = ALERT_PRESETS.filter((p) => p.kind === 'adversarial');
const COMBO_PRESETS = ALERT_PRESETS.filter((p) => p.kind === 'combo');

type tPresetGroupProps = {
  title: string;
  presets: tAlertPreset[];
  activePresetId: string | null;
  onApply: (preset: tAlertPreset) => void;
};

function PresetGroup({ title, presets, activePresetId, onApply }: tPresetGroupProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase font-semibold text-warning/80">
        {title}
      </span>
      <div className="flex flex-col gap-1 max-h-40 overflow-y-auto pr-1">
        {presets.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => onApply(preset)}
            className={classnames(
              'btn btn-xs btn-outline btn-warning whitespace-nowrap justify-start',
              { 'btn-active': activePresetId === preset.id },
            )}
            title={preset.id}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * DevToolbar-only preset panel (RET-1871). Imports `ALERT_PRESETS` and the
 * override store directly — never imported by `AlertCenter.hook.ts` — so it
 * stays out of the production bundle, tree-shaken alongside `DevToolbar`
 * (`Layout.provider.tsx`'s `IS_LOCAL_DEV` build-time literal).
 */
export default function AlertSimulatorPanel() {
  const override = useAlertSnapshotOverride();
  const activePresetId = override?.presetId ?? null;

  const handleApply = useCallback((preset: tAlertPreset) => {
    writeAlertSnapshotOverride({
      version: 1,
      presetId: preset.id,
      rawInputs: preset.override.rawInputs,
      patch: preset.override.patch,
    });
  }, []);

  const handleClear = useCallback(() => {
    writeAlertSnapshotOverride(null);
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase font-semibold text-warning/80">
          Alert simulator
        </span>
        <button
          type="button"
          onClick={handleClear}
          className="text-xs text-warning hover:underline"
          disabled={!override}
          title="Clear active preset and restore the real alert snapshot"
        >
          Clear
        </button>
      </div>

      {override && (
        <span className="text-[10px] text-warning/70 leading-tight">
          Active: <span className="font-mono">{activePresetId}</span>
        </span>
      )}

      <PresetGroup
        title="Declarations"
        presets={DECLARATION_PRESETS}
        activePresetId={activePresetId}
        onApply={handleApply}
      />
      <PresetGroup
        title="Adversarial"
        presets={ADVERSARIAL_PRESETS}
        activePresetId={activePresetId}
        onApply={handleApply}
      />
      <PresetGroup
        title="Combo"
        presets={COMBO_PRESETS}
        activePresetId={activePresetId}
        onApply={handleApply}
      />
    </div>
  );
}
